import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Button, 
  Paper, 
  Grid, 
  Card, 
  CardContent, 
  CardMedia, 
  Chip, 
  CircularProgress, 
  Alert,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent
} from '@mui/material';
import { 
  Search as SearchIcon, 
  Refresh as RefreshIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import { useSearchEbay } from '../hooks/useGoatCardsApi';
import { useTheme } from '../contexts/ThemeContext';
import { EBAY_SMART_LINK_CONFIG } from '../config/ebaySmartLink';

const EbayListingsPage: React.FC = () => {
  const { mode } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [displayLimit, setDisplayLimit] = useState(45); // Start with 45 cards
  const [currentOffset, setCurrentOffset] = useState(0); // Track current offset for pagination
  const [allLoadedItems, setAllLoadedItems] = useState<any[]>([]); // Store all loaded items
  const [selectedCardLimit, setSelectedCardLimit] = useState(45); // Selected number of cards to show
  const [isLoadingMore, setIsLoadingMore] = useState(false); // Separate loading state for pagination
  const [isPaginationRequest, setIsPaginationRequest] = useState(false); // Flag to track pagination vs new search
  const [totalLoadedItems, setTotalLoadedItems] = useState(0); // Track total items loaded
  
  // Goat Cards API hooks
  const ebaySearch = useSearchEbay();

  // Function to load default listings
  const loadDefaultListings = async () => {
    try {
      // Reset pagination state
      setCurrentOffset(0);
      setAllLoadedItems([]);
      setDisplayLimit(selectedCardLimit);
      setTotalLoadedItems(0);
      setIsLoadingMore(false);
      setIsPaginationRequest(false); // Ensure it's false for new search
      
      // Search for "Football PSA 10" by default with more results
      await ebaySearch.execute('Football PSA 10', selectedCardLimit, 0); // Request first batch of cards
    } catch (error) {
      console.error('Error loading default listings:', error);
    }
  };

  // Load initial listings with default criteria
  useEffect(() => {
    loadDefaultListings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount

  // Initialize smart link on component mount
  useEffect(() => {
    const initializeSmartLink = async () => {
      try {
        console.log('[Smart Link] Initializing smart link system...');
        await EBAY_SMART_LINK_CONFIG.initializeSmartLink();
        console.log('[Smart Link] Initialization complete:', EBAY_SMART_LINK_CONFIG.getStatus());
      } catch (error) {
        console.warn('[Smart Link] Initialization failed, will use fallback URLs:', error);
      }
    };

    initializeSmartLink();
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Reset pagination state
      setCurrentOffset(0);
      setAllLoadedItems([]);
      setDisplayLimit(selectedCardLimit);
      setTotalLoadedItems(0);
      setIsLoadingMore(false);
      setIsPaginationRequest(false); // Ensure it's false for new search
      
      // Search with the provided query and more results
      ebaySearch.execute(searchQuery.trim(), selectedCardLimit, 0); // Request first batch of cards
    } else {
      // Load default listings if no search term
      loadDefaultListings();
    }
  };

  const handleRefresh = () => {
    if (searchQuery.trim()) {
      // Reset pagination state
      setCurrentOffset(0);
      setAllLoadedItems([]);
      setDisplayLimit(selectedCardLimit);
      setTotalLoadedItems(0);
      setIsLoadingMore(false);
      setIsPaginationRequest(false); // Ensure it's false for new search
      
      ebaySearch.execute(searchQuery.trim(), selectedCardLimit, 0); // Request first batch of cards
    } else {
      loadDefaultListings();
    }
  };

  const handleFilterChange = (event: SelectChangeEvent) => {
    setFilter(event.target.value);
  };

  const handleSortChange = (event: SelectChangeEvent) => {
    setSortBy(event.target.value);
  };

  const handleCardLimitChange = (event: SelectChangeEvent) => {
    const newLimit = parseInt(event.target.value);
    setSelectedCardLimit(newLimit);
    setDisplayLimit(newLimit);
    setCurrentOffset(0);
    setAllLoadedItems([]);
    setTotalLoadedItems(0);
    setIsLoadingMore(false);
    setIsPaginationRequest(false); // Ensure it's false for new search
    
    // Reload data with new limit
    const searchTerm = searchQuery.trim() || 'Football PSA 10';
    ebaySearch.execute(searchTerm, newLimit, 0);
  };

  // Get current data from eBay search
  const currentData = ebaySearch.data;
  const isLoading = ebaySearch.loading;
  const error = ebaySearch.error;

  // Update allLoadedItems when new data arrives
  useEffect(() => {
    if (currentData) {
      // Try multiple possible data structures
      let data = null;
      let newItems: any[] = [];
      
      // Check if currentData has a data property
      if (currentData.data) {
        data = currentData.data;
      } else {
        data = currentData;
      }
      
      // Try to extract items from various possible structures
      if (data && Array.isArray(data.itemSummaries)) {
        newItems = data.itemSummaries;
      } else if (data && Array.isArray(data.items)) {
        newItems = data.items;
      } else if (data && Array.isArray(data.results)) {
        newItems = data.results;
      } else if (data && Array.isArray(data)) {
        newItems = data;
      } else if (data && typeof data === 'object') {
        // Try to find any array property that might contain items
        const keys = Object.keys(data);
        
        for (const key of keys) {
          if (Array.isArray(data[key]) && data[key].length > 0) {
            newItems = data[key];
            break;
          }
        }
      }
      
      if (newItems.length > 0) {
        if (isPaginationRequest) {
          // Pagination request - append new items to existing ones
          setAllLoadedItems(prev => {
            const updatedItems = [...prev, ...newItems];
            setTotalLoadedItems(updatedItems.length);
            return updatedItems;
          });
        } else {
          // New search or initial load - replace all items
          setAllLoadedItems(newItems);
          setTotalLoadedItems(newItems.length);
        }
      }
    }
  }, [currentData, isPaginationRequest]);

  // Update display limit when not loading and total loaded items change
  useEffect(() => {
    if (!isLoadingMore && totalLoadedItems > 0 && !isPaginationRequest) {
      // Only update display limit for new searches, not pagination
      setDisplayLimit(selectedCardLimit);
    }
  }, [totalLoadedItems, isLoadingMore, isPaginationRequest, selectedCardLimit]);

  // Filter and sort listings from accumulated data
  const filteredAndSortedListings = (() => {
    if (allLoadedItems.length === 0) {
      return [];
    }
    
    // Apply display limit to accumulated items
    const result = allLoadedItems.slice(0, displayLimit);
    return result;
  })();

  // Get total available items from API response
  const totalAvailableItems = (() => {
    if (!currentData) return allLoadedItems.length;
    const data = currentData.data || currentData;
    
    // Try to get total from API response
    if (data && typeof data.total === 'number') {
      return data.total;
    }
    if (currentData && typeof (currentData as any).total === 'number') {
      return (currentData as any).total;
    }
    
    // Fallback to current loaded items count
    return allLoadedItems.length;
  })();

  // Load more function
  const handleLoadMore = async () => {
    setIsLoadingMore(true); // Set loading state for pagination only
    const newOffset = currentOffset + selectedCardLimit; // Calculate next offset
    setCurrentOffset(newOffset);
    setIsPaginationRequest(true); // Set flag to true for pagination
    
    // Fetch next page of data
    const searchTerm = searchQuery.trim() || 'Football PSA 10';
    try {
      await ebaySearch.execute(searchTerm, selectedCardLimit, newOffset);
      // Increment display limit after successful data load
      setDisplayLimit(prev => prev + selectedCardLimit);
    } catch (error) {
      console.error('Error loading more items:', error);
    } finally {
      setIsLoadingMore(false); // Clear loading state
      setIsPaginationRequest(false); // Clear flag after request
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);
  };

  // Extract actual item ID from malformed format like "v1|277345685893|0"
  const extractItemId = (rawItemId: string): string | null => {
    console.log('[Item ID] extractItemId called with:', rawItemId);
    
    if (!rawItemId) {
      console.log('[Item ID] No rawItemId provided');
      return null;
    }
    
    // Check if it's in the malformed format "v1|123456789|0"
    const malformedMatch = rawItemId.match(/^v1\|(\d+)\|0$/);
    if (malformedMatch) {
      const actualItemId = malformedMatch[1];
      console.log('[Item ID] ✅ Extracted from malformed format:', { rawItemId, actualItemId });
      return actualItemId;
    }
    
    // Check if it's already a valid numeric item ID
    if (/^\d+$/.test(rawItemId)) {
      console.log('[Item ID] ✅ Already valid format:', rawItemId);
      return rawItemId;
    }
    
    // Try to extract any numeric ID from the string
    const numericMatch = rawItemId.match(/(\d{10,})/); // eBay item IDs are typically 10+ digits
    if (numericMatch) {
      const extractedId = numericMatch[1];
      console.log('[Item ID] ✅ Extracted numeric ID from string:', { rawItemId, extractedId });
      return extractedId;
    }
    
    console.warn('[Item ID] ❌ Could not extract valid item ID from:', rawItemId);
    return null;
  };



  if (isLoading && allLoadedItems.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 6, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress 
          size={60} 
          sx={{ 
            color: mode === 'light' ? 'primary.main' : '#FFFFFF' 
          }} 
        />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Box sx={{ mb: 6, maxWidth: '800px', mx: 'auto', textAlign: 'center' }}>
        <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
          Live eBay Listings
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          *We may receive commission on listings
        </Typography>
      </Box>

      {/* Search and Filter Controls */}
      <Box sx={{ mb: 4 }}>
        <form onSubmit={handleSearch}>
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Search for a player (e.g., 'Tom Brady')"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Button 
              type="submit" 
              variant="contained" 
              sx={{ minWidth: 120 }}
            >
              Search
            </Button>
            <Tooltip title="Refresh listings">
              <IconButton onClick={handleRefresh} sx={{ border: 1, borderColor: 'divider' }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </form>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Filter</InputLabel>
            <Select value={filter} label="Filter" onChange={handleFilterChange}>
              <MenuItem value="all">All Cards</MenuItem>
              <MenuItem value="hot">Hot Cards</MenuItem>
              <MenuItem value="graded">Graded Only</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Sort By</InputLabel>
            <Select value={sortBy} label="Sort By" onChange={handleSortChange}>
              <MenuItem value="newest">Newest First</MenuItem>
              <MenuItem value="oldest">Oldest First</MenuItem>
              <MenuItem value="priceLow">Price: Low to High</MenuItem>
              <MenuItem value="priceHigh">Price: High to Low</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Cards per Page</InputLabel>
            <Select value={selectedCardLimit.toString()} label="Cards per Page" onChange={handleCardLimitChange}>
              <MenuItem value={45}>45 Cards</MenuItem>
              <MenuItem value={90}>90 Cards</MenuItem>
              <MenuItem value={135}>135 Cards</MenuItem>
            </Select>
          </FormControl>

          <Button 
            variant="outlined" 
            onClick={() => {
              setSearchQuery('');
              setFilter('all');
              setSortBy('newest');
              setCurrentOffset(0);
              setAllLoadedItems([]);
              setDisplayLimit(selectedCardLimit);
              setTotalLoadedItems(0);
              setIsLoadingMore(false);
              setIsPaginationRequest(false); // Ensure it's false for new search
              loadDefaultListings();
            }}
          >
            Clear All
          </Button>
        </Box>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {/* Results Count */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Showing {filteredAndSortedListings.length} of {totalAvailableItems} cards
          {searchQuery && ` for "${searchQuery}"`}
          {!searchQuery && ' (Football PSA 10)'}
          {currentData?.data?.total && ` • Total Available: ${currentData.data.total}`}
        </Typography>
      </Box>

      {/* Cards Grid */}
      {filteredAndSortedListings.length > 0 ? (
        <Grid container spacing={3}>
          {filteredAndSortedListings.map((item: any, index: number) => {
            // Extract data with exact field names from eBay API response
            const title = item.title || 'Untitled';
            const price = item.price?.value || 0;
            const imageUrl = item.image?.imageUrl || '/placeholder-card.jpg';
            const condition = item.condition || 'Unknown';
            const rawItemId = item.itemId;
            const itemWebUrl = item.itemWebUrl || '#';
            
            console.log('[DEBUG] About to extract item ID from:', rawItemId);
            
            // Extract actual item ID from malformed format like "v1|277345685893|0"
            const itemId = extractItemId(rawItemId);
            
            // Generate smart link with fallback to itemWebUrl
            const itemUrl = itemId ? EBAY_SMART_LINK_CONFIG.generateSmartLink(itemId, itemWebUrl) : itemWebUrl;
            
            // Debug logging for Smart Links
            if (rawItemId) {
              console.log('[Smart Link] Raw Item ID:', rawItemId);
              console.log('[Smart Link] Extracted Item ID:', itemId);
              console.log('[Smart Link] Original itemWebUrl:', itemWebUrl);
              console.log('[Smart Link] Generated URL:', itemUrl);
              console.log('[Smart Link] Status:', EBAY_SMART_LINK_CONFIG.getStatus());
              console.log('[Smart Link] URL validation:', EBAY_SMART_LINK_CONFIG.isValidEbayUrl(itemUrl));
            }
            const seller = item.seller?.username || 'Unknown Seller';
            const feedbackScore = item.seller?.feedbackScore || 0;
            
            return (
              <Grid item xs={12} sm={6} md={4} key={item.itemId || index}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    transition: 'transform 0.2s ease-in-out',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                  onClick={() => {
                    if (itemUrl !== '#') {
                      window.open(itemUrl, '_blank', 'noopener,noreferrer');
                    }
                  }}
                >
                  <CardMedia
                    component="img"
                    height="250"
                    image={imageUrl}
                    alt={title}
                    onError={(e) => {
                      // Fallback to placeholder if image fails to load
                      (e.currentTarget as HTMLImageElement).src = '/placeholder-card.jpg';
                    }}
                    sx={{ 
                      objectFit: 'contain',
                      backgroundColor: mode === 'light' ? '#f8f9fa' : '#2d3748',
                      minHeight: '250px',
                      maxHeight: '300px',
                      width: '100%',
                      padding: '12px',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '& img': {
                        objectFit: 'contain',
                        maxWidth: '100%',
                        maxHeight: '100%',
                        width: 'auto',
                        height: 'auto',
                        borderRadius: '4px'
                      }
                    }}
                  />
                  
                  <CardContent sx={{ 
                    flexGrow: 1, 
                    display: 'flex', 
                    flexDirection: 'column',
                    '&:hover': {
                      backgroundColor: mode === 'light' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.02)'
                    }
                  }}>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, lineHeight: 1.2 }}>
                      {title && title.length > 60 
                        ? `${title.substring(0, 60)}...` 
                        : title
                      }
                    </Typography>
                    
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        mb: 1
                      }}>
                        <Typography variant="h4" sx={{ 
                          fontWeight: 700, 
                          color: mode === 'light' ? '#000000' : '#FFFFFF',
                          textShadow: '0 1px 2px rgba(0,0,0,0.1)'
                        }}>
                          {price ? formatPrice(parseFloat(price)) : 'N/A'}
                        </Typography>
                        {price && (
                          <Chip 
                            label="Current Price" 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                          />
                        )}
                      </Box>
                      {!price && (
                        <Typography variant="body2" color="error.main">
                          Price not available
                        </Typography>
                      )}
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      {condition && (
                        <Chip 
                          label={condition} 
                          size="small" 
                          sx={{ mr: 1, mb: 1 }}
                        />
                      )}
                      {feedbackScore > 1000 && (
                        <Chip 
                          label="Top Seller" 
                          size="small" 
                          color="success" 
                          sx={{ mr: 1, mb: 1 }}
                        />
                      )}
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Seller: {seller}
                        {feedbackScore > 0 && ` (${feedbackScore} feedback)`}
                      </Typography>
                    </Box>

                    <Box sx={{ mt: 'auto', pt: 2 }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        size="small"
                        endIcon={<OpenInNewIcon />}
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent card click
                          if (itemUrl !== '#') {
                            window.open(itemUrl, '_blank', 'noopener,noreferrer');
                          }
                        }}
                        disabled={itemUrl === '#'}
                        sx={{
                          fontSize: '0.875rem',
                          '&:hover': {
                            backgroundColor: 'primary.main',
                            color: 'white'
                          }
                        }}
                      >
                        {itemUrl !== '#' ? 'View on eBay' : 'Details Unavailable'}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            No cards found
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {searchQuery 
              ? `No cards found for "${searchQuery}". Try a different search term.`
              : 'No Football PSA 10 available at the moment. Please try again later.'
            }
          </Typography>
          <Button variant="contained" onClick={() => {
            setSearchQuery('');
            setCurrentOffset(0);
            setAllLoadedItems([]);
            setDisplayLimit(selectedCardLimit);
            setTotalLoadedItems(0);
            setIsLoadingMore(false);
            setIsPaginationRequest(false); // Ensure it's false for new search
            loadDefaultListings();
          }}>
            Clear Search
          </Button>
        </Paper>
      )}

      {/* Load More Button */}
      {filteredAndSortedListings.length > 0 && displayLimit < totalAvailableItems && !isLoadingMore && (
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Button
            variant="outlined"
            size="large"
            onClick={handleLoadMore}
            sx={{
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              borderWidth: 2,
              '&:hover': {
                borderWidth: 2,
                transform: 'translateY(-2px)',
                boxShadow: 2
              }
            }}
          >
            Load More Cards ({filteredAndSortedListings.length} of {totalAvailableItems})
          </Button>
        </Box>
      )}

      {/* Loading More Spinner */}
      {isLoadingMore && (
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
            <CircularProgress size={24} />
            <Typography variant="body1" color="text.secondary">
              Loading more cards...
            </Typography>
          </Box>
        </Box>
      )}

      {/* Show All Loaded Message */}
      {filteredAndSortedListings.length > 0 && displayLimit >= totalAvailableItems && totalAvailableItems > 0 && (
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            All {totalAvailableItems} cards loaded
          </Typography>
        </Box>
      )}

      {/* Footer */}
      <Box sx={{ mt: 6, textAlign: 'center' }}>
        <Divider sx={{ mb: 3 }} />
        <Typography variant="body2" color="text.secondary">
          Data provided by Goat Cards API • Prices and availability subject to change
        </Typography>
      </Box>
    </Container>
  );
};

export default EbayListingsPage; 